/**
 * Utility to parse AI chat responses and extract structured sections
 * Specifically designed to identify and extract Recommendations sections
 */

export interface ParsedRecommendationSection {
  type: 'immediate' | 'preventive' | 'longterm' | 'monitoring'
  title: string
  content: string[]
}

export interface ParsedChatResponse {
  contentBeforeRecommendations: string
  recommendations: ParsedRecommendationSection[]
  contentAfterRecommendations: string
}

/**
 * Parse a chat response to extract recommendations sections
 */
export function parseChatResponse(content: string): ParsedChatResponse {
  // Try to find the Recommendations section
  const recommendationsMatch = content.match(/Recommendations?:?\s*\n/i)

  if (!recommendationsMatch) {
    // No recommendations section found, return entire content as before
    return {
      contentBeforeRecommendations: content,
      recommendations: [],
      contentAfterRecommendations: ''
    }
  }

  const recommendationsIndex = recommendationsMatch.index! + recommendationsMatch[0].length
  const beforeRecommendations = content.substring(0, recommendationsMatch.index!).trim()
  const recommendationsText = content.substring(recommendationsIndex).trim()

  // Parse recommendation sections
  const recommendations: ParsedRecommendationSection[] = []

  // Patterns to match different recommendation types
  const patterns = [
    {
      type: 'immediate' as const,
      regex: /(?:^|\n)\s*(?:\d+\.\s*)?(?:Immediate\s+Actions?|Immediate\s+Action\s+Required?):?\s*\n/i
    },
    {
      type: 'preventive' as const,
      regex: /(?:^|\n)\s*(?:\d+\.\s*)?(?:Preventive\s+Measures?|Preventive\s+Actions?):?\s*\n/i
    },
    {
      type: 'longterm' as const,
      regex: /(?:^|\n)\s*(?:\d+\.\s*)?(?:Long[- ]term\s+Strategy|Long[- ]term\s+Actions?):?\s*\n/i
    },
    {
      type: 'monitoring' as const,
      regex: /(?:^|\n)\s*(?:Monitoring\s+Priorities?):?\s*\n/i
    }
  ]

  const foundSections: Array<{ type: ParsedRecommendationSection['type']; index: number; match: RegExpMatchArray }> = []

  // Find all section matches
  patterns.forEach(pattern => {
    const match = recommendationsText.match(pattern.regex)
    if (match && match.index !== undefined) {
      foundSections.push({ type: pattern.type, index: match.index, match })
    }
  })

  // Sort by index
  foundSections.sort((a, b) => a.index - b.index)

  // Extract content for each section
  for (let i = 0; i < foundSections.length; i++) {
    const section = foundSections[i]
    const nextSection = foundSections[i + 1]

    const sectionStart = section.index + section.match[0].length
    const sectionEnd = nextSection ? nextSection.index : recommendationsText.length

    const sectionContent = recommendationsText.substring(sectionStart, sectionEnd).trim()

    // Extract bullet points or numbered items
    const items = sectionContent
      .split(/\n/)
      .map(line => line.trim())
      .filter(line => {
        // Match bullet points (-, *, •) or numbered items, or lines that aren't empty
        // Skip lines that look like section headers
        if (!line || line.length === 0) return false
        if (/^(?:\d+\.\s*)?(?:Immediate|Preventive|Long[- ]term|Monitoring)/i.test(line)) return false

        return true
      })
      .map(line => {
        // Remove bullet points and numbering
        return line
          .replace(/^[-*•]\s+/, '')
          .replace(/^\d+\.\s+/, '')
          .trim()
      })
      .filter(line => line.length > 0 && !line.match(/^[A-Z][^:]*:$/)) // Filter out section headers

    if (items.length > 0) {
      recommendations.push({
        type: section.type,
        title: getSectionTitle(section.type),
        content: items
      })
    }
  }

  // Extract content after recommendations (if any)
  const lastSectionEnd =
    foundSections.length > 0
      ? foundSections[foundSections.length - 1].index +
        recommendationsText.substring(foundSections[foundSections.length - 1].index).match(/^[^\n]*\n/)![0].length
      : 0

  const afterRecommendations = recommendationsText.substring(foundSections.length > 0 ? lastSectionEnd : 0).trim()

  return {
    contentBeforeRecommendations: beforeRecommendations,
    recommendations,
    contentAfterRecommendations: afterRecommendations
  }
}

function getSectionTitle(type: ParsedRecommendationSection['type']): string {
  switch (type) {
    case 'immediate':
      return 'Immediate Actions'
    case 'preventive':
      return 'Preventive Measures'
    case 'longterm':
      return 'Long-term Strategy'
    case 'monitoring':
      return 'Monitoring Priorities'
    default:
      return 'Recommendations'
  }
}
